import { Injectable } from '@angular/core';
import { Order, QueryEntity } from '@datorama/akita';
import { LedgerAccountsStore, LedgerAccountsState } from './ledger-accounts.store';
import {
    checkFilter,
    flattenNestedObject,
    InferFilter,
    QueryFilterNew
} from '../../../../shared/state/query-filter.functions';
import { Observable } from 'rxjs';
import { get } from 'dot-prop';
import { LedgerAccount } from './ledger-account.model';
import { LedgerAccountsService } from './ledger-accounts.service';
import { filter, map, shareReplay, switchMap } from 'rxjs/operators';
import { BooksQuery } from '../../books/state/books.query';

@Injectable({ providedIn: 'root' })
export class LedgerAccountsQuery extends QueryEntity<LedgerAccountsState> {
    constructor(
        protected store: LedgerAccountsStore,
        private ledgerAccountsService: LedgerAccountsService,
        private _booksQuery: BooksQuery
    ) {
        super(store);
        this._booksQuery.activeBook$
            .pipe(
                switchMap((book) => {
                    return this.ledgerAccountsService.get(book.id);
                })
            )
            .subscribe();
    }

    all$ = this.selectAll();
    allAsObject$ = this.selectAll({ asObject: true });
    isLoading$ = this.selectLoading();

    getByNumber(accountNumber: number): LedgerAccount {
        return this.getAll().find(
            (ledgerAccount) => ledgerAccount.accountNo === accountNumber
        );
    }

    getById(id: LedgerAccount['id']): LedgerAccount {
        return this.getAll().find((ledgerAccount) => ledgerAccount.id === id);
    }

    hasLedgerAccounts$ = this.selectLoading().pipe(
        filter((loading) => !loading),
        switchMap((_) =>
            this.selectCount((ledgerAccount) => ledgerAccount.type !== 'Ignore').pipe(
                map((count) => count > 0),
                shareReplay(1)
            )
        ),
        shareReplay(1)
    );

    types$ = this.selectAll().pipe(
        map((ledgerAccounts) => {
            const types = ledgerAccounts
                .filter((ledgerAccount) => ledgerAccount.type !== 'Ignore')
                .map((ledgerAccount) => ledgerAccount.type);
            return [...new Set(types)];
        }),
        shareReplay(1)
    );

    selectById$(id: string): Observable<LedgerAccount> {
        return this.selectEntity(id);
    }

    selectLedgerAccountsByFilter$(
        filters: InferFilter<LedgerAccount> = {},
        sortBy: keyof LedgerAccount | any = 'accountNumber',
        sortByOrder: Order = Order.ASC
    ): Observable<LedgerAccount[]> {
        return this.selectAll({
            sortBy,
            sortByOrder,
            filterBy: (ledgerAccount) => {
                // ...flatten nested object and filter out null values
                const FILTERS = Object.entries<any>(flattenNestedObject(filters)).filter(
                    (filter) => filter[1] !== null
                );
                if (FILTERS.length > 0) {
                    return !FILTERS.find(([key, criteria]) => {
                        const value = get<string | number>(ledgerAccount, key);
                        return !checkFilter(value, criteria);
                    });
                } else {
                    return true;
                }
            }
        });
    }
}
